import React from 'react'

import { Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, oneOf, shape, string,
} from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import PlanOptionCard from './PlanOptionCard'
import useSwitchPlanModal from './useSwitchPlanModal'
import { PLAN_TYPE } from '../../constants'
import { plansShape } from '../../propTypes'

const MOBILE_BREAKPOINTS = ['xs', 'sm']

function SwitchPlanModal({
  isOpen,
  onClose,
  subscription,
  plans,
  previewData,
  locale = 'en',
  onSwitchSuccess = NOOP,
}) {
  const {
    currentPlan,
    targetPlan,
    noticeMessage,
    primaryButtonText,
    cancelButtonText,
    title,
    isSubmitDisabled,
    handleClose,
    handleSubmit,
  } = useSwitchPlanModal({
    subscription, locale, onClose, previewData, onSwitchSuccess,
  })

  const breakpoint = useBreakpoint()
  const buttonSize = MOBILE_BREAKPOINTS.includes(breakpoint) ? 'small' : 'medium'

  const translate = (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const getPriceCents = (plan) => (
    plan.planType === currentPlan
      ? Math.round(previewData.current_price * 100)
      : Math.round(previewData.target_price * 100)
  )

  const renderPlanCard = (plan) => (
    <PlanOptionCard
      key={plan.planType}
      name={translate(`subscription_management.switch_plan_modal.plan.${plan.planType}.name`)}
      subtitle={translate(`subscription_management.switch_plan_modal.plan.${plan.planType}.subtitle`)}
      priceCents={getPriceCents(plan)}
      currency={plan.currency}
      priceUnit={translate('subscription_management.switch_plan_section.price_unit')}
      tag={translate(`subscription_management.switch_plan_section.tags.${plan.planType}`)}
      tagColor={plan.planType === PLAN_TYPE.YEARLY ? 'primary' : 'grey'}
      isCurrent={currentPlan === plan.planType}
      isSelected={targetPlan === plan.planType}
      locale={locale}
    />
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header={title}
      width="medium"
      canClose
      customClass="switch-plan-modal"
      primaryAction={{
        content: primaryButtonText,
        buttonProps: {
          type: 'primary', onClick: handleSubmit, size: buttonSize, disabled: isSubmitDisabled,
        },
      }}
      secondaryAction={{
        content: cancelButtonText,
        buttonProps: { onClick: handleClose, size: buttonSize },
      }}
    >
      <div className="switch-plan-modal__body">
        <div className="switch-plan-modal__plans">
          {plans.map(renderPlanCard)}
        </div>
        <p className="switch-plan-modal__notice cgt-body-base cgt-mb-0" role="status">
          {noticeMessage}
        </p>
      </div>
    </Modal>
  )
}

SwitchPlanModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  subscription: shape({
    id: number,
    planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
    subscriptionEndDate: string,
  }).isRequired,
  plans: plansShape.isRequired,
  previewData: shape({
    current_plan_type: string,
    target_plan_type: string,
    effective_at: string,
    current_price: number,
    target_price: number,
  }).isRequired,
  locale: string,
  onSwitchSuccess: func,
}

export default SwitchPlanModal
